import React, { useMemo } from 'react';
import { Logger } from '@saviynt/common';
import { Box, Icon, type IconKind, Typography } from '@saviynt/design-system';

import type { KpiSummaryResponse } from '../../services/KpiSummaryService';
import type { InvertedLocalize } from '../../utils/localization';

import { useKpiSummary } from '../../hooks/use-kpisummary';
import { useInvertedLocalize } from '../../utils/localization';

import KpiTile from './KpiTile/KpiTile';
import invertedMessages, { type KpiTilesMessage } from './messages';

import './KpiTiles.css';

// types for KPI data
export interface KpiData {
  id: string;
  title: string;
  value: string | number;
  subtitle: string;
  // TODO: use change/delta in future mvp
  // change: {
  //   value: string;
  //   type: 'positive' | 'negative';
  // };
  icon: IconKind;
}

// Single source of truth for static KPI tile metadata
const KPI_TILE_METADATA = [
  {
    id: 'pending-recommendations',
    title: 'Pending Recommendations' as const,
    icon: 'Time' as const,
    apiKey: 'pendingRecommendations' as const,
  },
  {
    id: 'active-recommended-roles',
    title: 'Active Recommended Roles' as const,
    icon: 'QuickAction' as const,
    apiKey: 'assignedAccess' as const,
  },
  {
    id: 'total-active-roles',
    title: 'Total Active Roles' as const,
    icon: 'Group' as const,
    apiKey: 'activeRoles' as const,
  },
] as const;

// type for localization function with placeholders
type LocalizeFn = InvertedLocalize<KpiTilesMessage>;

/**
 * Helper function to generate pending recommendations subtitle
 */
export function getPendingRecsSubtitle(
  pendingData: { label?: string | null },
  l: LocalizeFn
): string {
  const count = Number(pendingData.label) || 0;

  return l('Covers {count} assigned access', { count });
}

/**
 * Helper function to generate total active roles subtitle
 */
export function getTotalActiveRolesSubtitle(
  activeRolesData: { label?: string | null },
  l: LocalizeFn
): string {
  const count = Number(activeRolesData.label) || 0;

  return l('{count} Assigned access from these Roles', { count });
}

/**
 * Helper function to generate assigned access subtitle
 */
export function getAssignedAccessSubtitle(
  assignedAccessData: { label?: string | null },
  l: LocalizeFn
): string {
  const count = Number(assignedAccessData.label) || 0;

  return l('{count} Assigned access from these Roles', { count });
}

/**
 * Transform API response to KpiData format using metadata configuration
 */
export function transformKpiData(
  apiData: KpiSummaryResponse,
  l: LocalizeFn
): KpiData[] {
  const { summary } = apiData;

  return KPI_TILE_METADATA.map((metadata) => {
    let subtitle: string;

    // NOTE: workforceTimeSaved check disabled - uncomment if tile is re-enabled
    if (metadata.apiKey === 'pendingRecommendations') {
      subtitle = getPendingRecsSubtitle(summary[metadata.apiKey], l);
    } else if (metadata.apiKey === 'activeRoles') {
      subtitle = getTotalActiveRolesSubtitle(summary[metadata.apiKey], l);
    } else {
      subtitle = getAssignedAccessSubtitle(summary[metadata.apiKey], l);
    }

    return {
      ...metadata,
      title: l(metadata.title),
      value: summary[metadata.apiKey].count,
      subtitle,
      change: {
        value: summary[metadata.apiKey].delta, // Delta included for future use
        type: 'positive',
      },
    };
  });
}

function KpiTiles() {
  // localization setup
  const l = useInvertedLocalize(invertedMessages);

  const { data: apiData, isLoading, error } = useKpiSummary();

  // Determine which tiles to render based on loading state and data availability
  const tilesToRender = useMemo(() => {
    if (isLoading) {
      // Show skeleton tiles with localized titles
      return KPI_TILE_METADATA.map((metadata) => ({
        ...metadata,
        title: l(metadata.title),
      }));
    }

    // Handle all error cases here
    if (error) {
      return null;
    }

    if (!apiData?.summary) {
      return []; // empty state for no data
    }

    return transformKpiData(apiData, l);
  }, [apiData, isLoading, error, l]);

  // handle error state
  if (error) {
    Logger.error('KPI tiles API error', error);

    return (
      <Box tag='section' className='Roles-KpiTiles Roles-KpiTiles--error'>
        <div>
          <Icon
            kind='AlertWarningFilled'
            size='xLarge'
            color='--color-background-warning-bold'
          />
        </div>
        <Typography
          kind='body2-bold'
          color='--color-foreground-neutral-bold'
          className='Dashboard-filteredZeroRolesMessage'>
          {l('Unable to load KPI data')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box tag='section' className='Roles-KpiTiles'>
      {tilesToRender?.map((tile) => (
        <KpiTile key={tile.id} data={tile} isLoading={isLoading} />
      ))}
    </Box>
  );
}

export default KpiTiles;
